// src/slices/unsoldTicketReturn/reducer.ts
// Redux slice cho US-016/US-017 — tiếp nhận, kiểm đếm vé ế tại quầy & lập phiếu đối soát

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  MATCH_STATUS,
  MATCH_STATUS_LABELS,
  ScanCountResult,
  UnsoldTicketReturnFilters,
  UnsoldTicketReturnModalState,
  UnsoldTicketReturnRequest,
} from 'pages/UnsoldTicketCounterReview/types';
import { TicketReturnReconciliation } from 'slices/ticketReturnReconciliation/types';
import { UnsoldTicketReturnState } from './types';
import {
  fetchUnsoldTicketReturnRequests,
  fetchUnsoldTicketReturnRequestById,
  createUnsoldTicketReturnRequest,
  scanCountUnsoldTicket,
  fetchScanReconciliation,
  createTicketReturnReconciliation,
} from './thunk';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('unsold_ticket_return_messages');

const initialFilters: UnsoldTicketReturnFilters = {
  agentId: '',
  code: '',
  status: 'All',
};

const initialState: UnsoldTicketReturnState = {
  list: [],
  selectedItem: null,
  scanResult: null,
  scanReconciliation: null,
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: { ...initialFilters },
  loading: false,
  loadingDetail: false,
  scanning: false,
  submitting: false,
  error: null,
  scanLogs: [],
  modalState: {
    createReconciliation: false,
  },
};

const unsoldTicketReturnSlice = createSlice({
  name: 'UnsoldTicketReturn',
  initialState,
  reducers: {
    // Chọn/đóng 1 phiên kiểm đếm — reset luôn kết quả quét của phiên trước
    setSelectedItem: (state, action: PayloadAction<UnsoldTicketReturnRequest | null>) => {
      state.selectedItem = action.payload;
      state.scanResult = null;
      state.scanReconciliation = null;
      state.scanLogs = [];
    },
    setFilters: (state, action: PayloadAction<Partial<UnsoldTicketReturnFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.pagination.currentPage = 1;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    clearScanLogs: (state) => {
      state.scanLogs = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof UnsoldTicketReturnModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof UnsoldTicketReturnModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Tìm kiếm yêu cầu hoàn vé ế
      .addCase(fetchUnsoldTicketReturnRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnsoldTicketReturnRequests.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.list = payload?.items || payload?.data || payload || [];
        state.pagination.totalItems = payload?.totalItems ?? payload?.totalCount ?? state.list.length;
        state.pagination.totalPages =
          payload?.totalPages ??
          Math.ceil(state.pagination.totalItems / Math.max(state.pagination.pageSize, 1));
      })
      .addCase(fetchUnsoldTicketReturnRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Chi tiết yêu cầu
      .addCase(fetchUnsoldTicketReturnRequestById.pending, (state) => {
        state.loadingDetail = true;
      })
      .addCase(fetchUnsoldTicketReturnRequestById.fulfilled, (state, action) => {
        state.loadingDetail = false;
        state.selectedItem = action.payload as UnsoldTicketReturnRequest;
      })
      .addCase(fetchUnsoldTicketReturnRequestById.rejected, (state, action) => {
        state.loadingDetail = false;
        state.error = action.payload as string;
      })

      // Tạo yêu cầu hoàn vé ế
      .addCase(createUnsoldTicketReturnRequest.pending, (state) => {
        state.submitting = true;
      })
      .addCase(createUnsoldTicketReturnRequest.fulfilled, (state) => {
        state.submitting = false;
      })
      .addCase(createUnsoldTicketReturnRequest.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Quét đếm vé — lấy nguyên counters do server tính (declared/scanned/valid/rejected/remaining)
      .addCase(scanCountUnsoldTicket.pending, (state) => {
        state.scanning = true;
      })
      .addCase(scanCountUnsoldTicket.fulfilled, (state, action) => {
        state.scanning = false;
        const payload = action.payload as ScanCountResult;
        state.scanResult = payload;

        const scannedAt = moment().format('HH:mm:ss');
        // `scanned` chỉ chứa kết quả của các vé vừa gửi trong request này
        (payload?.scanned || []).forEach((line) => {
          state.scanLogs.unshift({
            ticketNumber: line.ticketNumber,
            symbol: line.symbol,
            matchStatus: line.matchStatus,
            matchStatusName:
              line.matchStatusName || MATCH_STATUS_LABELS[String(line.matchStatus)] || '',
            message: line.rejectReasonName || line.rejectReason || st('da_ghi_nhan_text'),
            scannedAt,
          });
        });
        state.scanLogs = state.scanLogs.slice(0, 30);
      })
      .addCase(scanCountUnsoldTicket.rejected, (state, action) => {
        state.scanning = false;
        const payload = action.payload as any;
        state.scanLogs.unshift({
          ticketNumber: payload?.ticketNumber || '',
          symbol: '',
          matchStatus: MATCH_STATUS.KhongHopLe,
          matchStatusName: MATCH_STATUS_LABELS[String(MATCH_STATUS.KhongHopLe)],
          message: payload?.message || st('khong_ghi_nhan_duoc_ve_vua_text'),
          scannedAt: moment().format('HH:mm:ss'),
        });
        state.scanLogs = state.scanLogs.slice(0, 30);
      })

      // Phiếu đối soát đang mở (toàn bộ vé đã quét cộng dồn)
      .addCase(fetchScanReconciliation.fulfilled, (state, action) => {
        state.scanReconciliation = action.payload as TicketReturnReconciliation;
      })

      // Lập phiếu đối soát
      .addCase(createTicketReturnReconciliation.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createTicketReturnReconciliation.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.createReconciliation = false;
      })
      .addCase(createTicketReturnReconciliation.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedItem,
  setFilters,
  clearFilters,
  setPage,
  clearScanLogs,
  clearError,
  openModal,
  closeModal,
} = unsoldTicketReturnSlice.actions;

export default unsoldTicketReturnSlice.reducer;
